"use client";

import { motion } from "framer-motion";
import { Button } from "./ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Badge } from "./ui/badge";
import { Calendar, Building2, Clock, IndianRupee } from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface Plan {
  _id: string;
  planName: string;
  noOfCompany: number;
  planType: string;
  duration: number;
  amount: number;
  createdDate: string;
}

interface PlanTableViewProps {
  plans: Plan[];
  purchasing: string | null;
  handlePurchase: (planId: string) => Promise<void>;
  getPlanIcon: (planType: string) => LucideIcon;
  getPlanColor: (index: number) => string;
}

export default function PlanTableView({
  plans,
  purchasing,
  handlePurchase,
  getPlanIcon,
  getPlanColor,
}: PlanTableViewProps) {
  const getPlanTypeVariant = (planType: string) => {
    switch (planType.toLowerCase()) {
      case "single":
        return "bg-blue-100 text-blue-800 hover:bg-blue-200";
      case "multi":
        return "bg-purple-100 text-purple-800 hover:bg-purple-200";
      default:
        return "bg-gray-100 text-gray-800 hover:bg-gray-200";
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200"
    >
      <div className="bg-gradient-to-r from-gray-50 to-white p-6 border-b border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Available Plans
        </h2>
        <p className="text-gray-600">
          Choose the perfect plan for your business needs
        </p>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50/50 border-b border-gray-200">
              <TableHead className="w-[200px] text-gray-900 font-semibold text-sm uppercase tracking-wide py-4 px-6">
                Plan Details
              </TableHead>
              <TableHead className="text-gray-900 font-semibold text-sm uppercase tracking-wide py-4 px-6">
                Type
              </TableHead>
              <TableHead className="text-gray-900 font-semibold text-sm uppercase tracking-wide py-4 px-6">
                Companies
              </TableHead>
              <TableHead className="text-gray-900 font-semibold text-sm uppercase tracking-wide py-4 px-6">
                Duration
              </TableHead>
              <TableHead className="text-gray-900 font-semibold text-sm uppercase tracking-wide py-4 px-6">
                Price
              </TableHead>
              <TableHead className="text-gray-900 font-semibold text-sm uppercase tracking-wide py-4 px-6">
                Created
              </TableHead>
              <TableHead className="text-right text-gray-900 font-semibold text-sm uppercase tracking-wide py-4 px-6">
                Action
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {plans.map((plan, index) => {
              const IconComponent = getPlanIcon(plan.planType);
              const colorClass = getPlanColor(index);

              return (
                <motion.tr
                  key={plan._id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="border-b border-gray-100 hover:bg-gray-50/50 transition-all duration-200 group"
                >
                  <TableCell className="py-6 px-6">
                    <div className="flex items-center space-x-3">
                      <div
                        className={`p-2 rounded-lg bg-gradient-to-r ${colorClass} text-white shadow-md`}
                      >
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 text-base">
                          {plan.planName}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          ID: {plan._id.slice(-6)}
                        </div>
                      </div>
                    </div>
                  </TableCell>

                  <TableCell className="py-6 px-6">
                    <Badge
                      variant="secondary"
                      className={`${getPlanTypeVariant(
                        plan.planType
                      )} font-medium px-3 py-1 text-xs uppercase tracking-wide`}
                    >
                      {plan.planType}
                    </Badge>
                  </TableCell>

                  <TableCell className="py-6 px-6">
                    <div className="flex items-center space-x-2">
                      <Building2 className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900 font-medium">
                        {plan.noOfCompany}
                      </span>
                      <span className="text-gray-500 text-sm">
                        {plan.noOfCompany === 1 ? "company" : "companies"}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell className="py-6 px-6">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900 font-medium">
                        {plan.duration}
                      </span>
                      <span className="text-gray-500 text-sm">
                        {plan.duration === 1 ? "day" : "days"}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell className="py-6 px-6">
                    <div className="flex items-center space-x-1">
                      <IndianRupee className="w-4 h-4 text-green-600" />
                      <span className="text-xl font-bold text-green-600">
                        {formatCurrency(plan.amount)}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell className="py-6 px-6">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600 text-sm">
                        {plan.createdDate}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell className="py-6 px-6 text-right">
                    <Button
                      onClick={() => handlePurchase(plan._id)}
                      disabled={purchasing === plan._id}
                      className={`
                        relative overflow-hidden px-6 py-2.5 rounded-lg font-semibold text-sm
                        bg-gradient-to-r ${colorClass} text-white shadow-md
                        hover:shadow-lg hover:scale-105 
                        disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
                        transition-all duration-200 ease-in-out
                        group-hover:shadow-xl
                      `}
                    >
                      {purchasing === plan._id ? (
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Processing...</span>
                        </div>
                      ) : (
                        <span>Purchase Plan</span>
                      )}
                    </Button>
                  </TableCell>
                </motion.tr>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {plans.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-gray-500 text-lg font-medium">
            No plans available
          </p>
          <p className="text-gray-400 text-sm mt-1">
            Check back later for new plans
          </p>
        </div>
      )}
    </motion.div>
  );
}
